// Слот скелета, картинку которого рисует сама игра.
//
// Художник оставляет в атласе образец, а игра ставит на его место свою картинку. Так набирается
// число на счётчике общего множителя: цифр в атласе панели нет вовсе, лежат два числа образца,
// см. features/steamSpin/totalMultiplier.ts.
//
// Картинка ложится на сетку слота целиком, как ложился на неё кадр атласа. Поэтому рисовать её
// надо той же формы, что и образец: сетка картинку не подгоняет, а растягивает.
//
// Сетки лежат в разобранном скелете, а он один на все объекты игры. Поэтому нарисованная
// картинка видна во всех объектах этого скелета, сколько бы их ни было на экране.

import { MeshAttachment, SpineTexture, TextureRegion, type Attachment, type Slot } from '@esotericsoftware/spine-pixi-v8';
import type { TextureSource } from 'pixi.js';

import type { Spine } from './manager';

/** Слот скелета, на котором игра рисует свою картинку. */
export class PaintedSlot {
  readonly #slot: Slot;
  readonly #mesh: MeshAttachment;

  /** @throws Error если такого слота в скелете нет или он держит не сетку */
  constructor(spine: Spine, name: string) {
    const slot: Slot | null = spine.skeleton.findSlot(name);
    if (slot === null)
      throw new Error(`В скелете ${spine.skeleton.data.name} нет слота "${name}"`);

    const attachment: Attachment | null = slot.getAttachment();
    if (!(attachment instanceof MeshAttachment))
      throw new Error(`Слот "${name}" держит не сетку: рисовать на нём нечем`);

    this.#slot = slot;
    this.#mesh = attachment;
  }

  /**
   * Рисует картинку на слоте.
   *
   * Область берётся во все края картинки: сетка получает её целиком, а не кадром из атласа.
   *
   * Заодно сетка возвращается на слот: анимация могла снять её с него, а картинка нужна
   * и после анимации.
   */
  paint(texture: TextureSource): void {
    const region = new TextureRegion();

    region.texture = SpineTexture.from(texture);
    region.u = 0;
    region.v = 0;
    region.u2 = 1;
    region.v2 = 1;
    region.width = texture.width;
    region.height = texture.height;
    region.originalWidth = texture.width;
    region.originalHeight = texture.height;
    region.offsetX = 0;
    region.offsetY = 0;
    region.degrees = 0;

    this.#mesh.region = region;
    this.#mesh.updateRegion();

    this.#slot.setAttachment(this.#mesh);
  }
}
